import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Info } from 'lucide-react';
import { appointments as appointmentsApi, groomers as groomersApi, timeOff as timeOffApi, services as servicesApi, calendarAnnotations as annotationsApi } from '../utils/api';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { formatTo12Hour } from '../utils/timeFormat';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AvailabilityCalendar = () => {
  const { isAdmin, isGroomer } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [capacityData, setCapacityData] = useState({});
  const [groomers, setGroomers] = useState([]);
  const [services, setServices] = useState([]);
  const [timeOffData, setTimeOffData] = useState([]);
  const [shopClosures, setShopClosures] = useState([]);
  // Roadmap item #18: informational annotations pinned to dates.
  const [annotations, setAnnotations] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [groomerSchedules, setGroomerSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingCapacity, setLoadingCapacity] = useState(false);
  const [viewMode, setViewMode] = useState('capacity'); // 'capacity' or 'groomers'

  useEffect(() => {
    loadInitialData();
    
    // Listen for service updates
    const handleServiceUpdate = () => {
      console.log('[AvailabilityCalendar] Received serviceUpdated event, reloading services');
      loadInitialData();
    };
    
    window.addEventListener('serviceUpdated', handleServiceUpdate);
    
    return () => {
      window.removeEventListener('serviceUpdated', handleServiceUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMonthCapacity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const loadInitialData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (isGroomer && !isAdmin) {
        // Groomer: load only their own record and schedule
        const [myGroomerRes, servicesRes, timeOffRes, closuresRes, businessHoursRes, annotationsRes] = await Promise.all([
          groomersApi.getMe(),
          servicesApi.getAll(),
          timeOffApi.getCalendar(),
          axios.get(`${API_URL}/shop-closures`, { headers }),
          axios.get(`${API_URL}/business-hours`, { headers }),
          annotationsApi.list().catch(() => ({ data: [] })),
        ]);

        const myGroomer = myGroomerRes.data;
        setGroomers([myGroomer]);
        setServices(servicesRes.data);
        setTimeOffData(timeOffRes.data);
        setShopClosures(closuresRes.data);
        setAnnotations(annotationsRes.data || []);
        setBusinessHours(businessHoursRes.data || []);

        // Load just this groomer's schedule
        const myScheduleRes = await axios.get(`${API_URL}/business-hours/groomers/${myGroomer.id}`, { headers });
        const schedMap = {};
        (myScheduleRes.data || []).forEach(entry => {
          if (!schedMap[entry.groomer_id]) schedMap[entry.groomer_id] = {};
          if (!schedMap[entry.groomer_id][entry.day_of_week]) schedMap[entry.groomer_id][entry.day_of_week] = [];
          if (entry.is_active) {
            schedMap[entry.groomer_id][entry.day_of_week].push(entry);
          }
        });
        setGroomerSchedules(schedMap);
      } else if (isAdmin) {
        // Admin: load all groomers and all schedules
        const [groomersRes, servicesRes, timeOffRes, closuresRes, businessHoursRes, groomerSchedulesRes, annotationsRes] = await Promise.all([
          groomersApi.getAll(),
          servicesApi.getAll(),
          timeOffApi.getCalendar(),
          axios.get(`${API_URL}/shop-closures`, { headers }),
          axios.get(`${API_URL}/business-hours`, { headers }),
          axios.get(`${API_URL}/business-hours/groomers`, { headers }),
          annotationsApi.list().catch(() => ({ data: [] })),
        ]);

        setGroomers(groomersRes.data);
        setServices(servicesRes.data);
        setTimeOffData(timeOffRes.data);
        setShopClosures(closuresRes.data);
        setAnnotations(annotationsRes.data || []);
        setBusinessHours(businessHoursRes.data || []);

        const schedMap = {};
        (groomerSchedulesRes.data || []).forEach(entry => {
          if (!schedMap[entry.groomer_id]) schedMap[entry.groomer_id] = {};
          if (!schedMap[entry.groomer_id][entry.day_of_week]) schedMap[entry.groomer_id][entry.day_of_week] = [];
          if (entry.is_active) {
            schedMap[entry.groomer_id][entry.day_of_week].push(entry);
          }
        });
        setGroomerSchedules(schedMap);
      } else {
        // Client: only load what's needed for capacity view.
        // Annotations are auth-gated but harmless to clients, so we
        // include them — an admin who wants to keep notes private
        // simply doesn't create them with sensitive content.
        const [servicesRes, closuresRes, businessHoursRes, annotationsRes] = await Promise.all([
          servicesApi.getAll(),
          axios.get(`${API_URL}/shop-closures`, { headers }),
          axios.get(`${API_URL}/business-hours`, { headers }),
          annotationsApi.list().catch(() => ({ data: [] })),
        ]);

        setServices(servicesRes.data);
        setShopClosures(closuresRes.data);
        setAnnotations(annotationsRes.data || []);
        setBusinessHours(businessHoursRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load availability data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthCapacity = async () => {
    setLoadingCapacity(true);
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    const capacityMap = {};
    const dates = [];
    let currentDate = calendarStart;

    // Collect all dates
    while (currentDate <= calendarEnd) {
      dates.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }

    // Load capacity for all dates in parallel
    try {
      const results = await Promise.allSettled(
        dates.map(date => {
          const dateStr = format(date, 'yyyy-MM-dd');
          return appointmentsApi.getCapacity(dateStr)
            .then(response => ({ dateStr, data: response.data }))
            .catch(error => {
              console.error(`Failed to load capacity for ${dateStr}:`, error.response?.data || error.message);
              return { dateStr, data: null };
            });
        })
      );

      // Process results
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value.data) {
          capacityMap[result.value.dateStr] = result.value.data;
        }
      });

      setCapacityData(capacityMap);
    } catch (error) {
      console.error('Error loading month capacity:', error);
      toast.error('Failed to load availability data');
    } finally {
      setLoadingCapacity(false);
    }
  };

  const getTimeOffForDate = (date, groomerId = null) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    return timeOffData.filter(timeOff => {
      // Check if groomer matches (if specified)
      if (groomerId && timeOff.groomer_id !== groomerId) {
        return false;
      }
      
      // Extract date strings without timezone conversion
      const startDateStr = timeOff.start_date.split('T')[0];
      const endDateStr = timeOff.end_date.split('T')[0];
      
      // Compare date strings directly
      return dateStr >= startDateStr && dateStr <= endDateStr;
    });
  };

  const getAvailableGroomersCount = (date) => {
    // Business closed = 0 available
    if (isDateClosed(date)) return 0;

    const dayOfWeek = date.getDay();

    // Count groomers who: work on this day + not on time off
    const availableCount = groomers.filter(groomer => {
      // Check if groomer has schedule entries for this day of week
      if (!isGroomerWorkingOnDay(groomer.id, dayOfWeek)) return false;
      // Check time off
      const timeOff = getTimeOffForDate(date, groomer.id);
      return timeOff.length === 0;
    }).length;
    
    return availableCount;
  };

  const renderCalendarHeader = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {(isAdmin || isGroomer) && (
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('capacity')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'capacity'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Capacity View
            </button>
            <button
              onClick={() => setViewMode('groomers')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'groomers'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Employee View
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCalendarDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="grid grid-cols-7 gap-2 mb-2">
        {days.map(day => (
          <div key={day} className="text-center font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const getCapacityStatus = (dateStr) => {
    const data = capacityData[dateStr];
    if (!data) return null;

    const capacity = data.capacity;
    // Use actual size categories from the capacity data instead of hardcoded array
    const sizes = Object.keys(capacity);
    
    let totalAvailable = 0;
    let totalCapacity = 0;

    sizes.forEach(size => {
      if (capacity[size]) {
        totalAvailable += capacity[size].available;
        totalCapacity += capacity[size].limit;
      }
    });

    const percentBooked = totalCapacity > 0 ? ((totalCapacity - totalAvailable) / totalCapacity) * 100 : 0;

    return {
      totalAvailable,
      totalCapacity,
      percentBooked,
      isFull: totalAvailable === 0,
      capacity
    };
  };

  // Check if the business is closed on a given day of week (from business hours settings)
  const isBusinessClosedOnDay = (dayOfWeek) => {
    if (businessHours.length === 0) return false; // No hours set = assume open
    const dayHours = businessHours.find(h => h.day_of_week === dayOfWeek);
    if (!dayHours) return false; // No entry = assume open
    // is_active comes from the API (aliased from is_open)
    return !dayHours.is_active;
  };

  // Check if a date is closed (shop closure OR regular business hours)
  const isDateClosed = (day) => {
    // First check regular business hours (e.g. weekends off)
    const dayOfWeek = day.getDay(); // 0=Sun, 6=Sat
    if (isBusinessClosedOnDay(dayOfWeek)) return true;

    // Then check specific shop closures
    const dateStr = format(day, 'yyyy-MM-dd');
    const month = day.getMonth() + 1;
    const dayNum = day.getDate();

    return shopClosures.some(closure => {
      const closureDate = closure.closure_date.split('T')[0];
      if (closureDate === dateStr) return true;
      if (closure.is_recurring && 
          closure.recurrence_month === month && 
          closure.recurrence_day === dayNum) {
        return true;
      }
      return false;
    });
  };

  // Check if a specific groomer works on a given day of week
  const isGroomerWorkingOnDay = (groomerId, dayOfWeek) => {
    const schedule = groomerSchedules[groomerId];
    if (!schedule) return false; // No schedule set = not working
    const daySlots = schedule[dayOfWeek];
    return daySlots && daySlots.length > 0;
  };

  // Roadmap item #18: returns annotations matching the given Date,
  // including yearly-recurring ones. Both the calendar cells and the
  // selected-day detail card use this.
  const getAnnotationsForDate = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const month = day.getMonth() + 1;
    const dayNum = day.getDate();
    return (annotations || []).filter((a) => {
      const aDate = String(a.annotation_date).split('T')[0];
      if (aDate === dateStr) return true;
      if (a.is_recurring && a.recurrence_month === month && a.recurrence_day === dayNum) return true;
      return false;
    });
  };

  // Maps the annotation color token to a Tailwind background class
  // (kept in sync with CalendarAnnotationsManager's COLOR_OPTIONS).
  const annotationDotClass = (color) => {
    switch (color) {
      case 'green':  return 'bg-green-500';
      case 'purple': return 'bg-purple-500';
      case 'orange': return 'bg-orange-500';
      case 'red':    return 'bg-red-500';
      case 'gray':   return 'bg-gray-500';
      case 'blue':
      default:       return 'bg-blue-500';
    }
  };

  // Get closure reason for a date
  const getClosureReason = (day) => {
    const dayOfWeek = day.getDay();
    if (isBusinessClosedOnDay(dayOfWeek)) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `Closed on ${dayNames[dayOfWeek]}s`;
    }

    const dateStr = format(day, 'yyyy-MM-dd');
    const month = day.getMonth() + 1;
    const dayNum = day.getDate();

    const closure = shopClosures.find(c => {
      const closureDate = c.closure_date.split('T')[0];
      if (closureDate === dateStr) return true;
      if (c.is_recurring && c.recurrence_month === month && c.recurrence_day === dayNum) return true;
      return false;
    });

    return closure?.reason || 'Shop Closed';
  };

  const renderCapacityCell = (day, dateStr, isCurrentMonth) => {
    const status = getCapacityStatus(dateStr);
    const isToday = isSameDay(day, new Date());
    const isSelected = isSameDay(day, selectedDate);
    const isClosed = isDateClosed(day);

    let bgColor = 'bg-white';
    let borderColor = 'border-gray-200';
    
    // Shop closed takes priority
    if (isClosed) {
      bgColor = 'bg-gray-200';
      borderColor = 'border-gray-400';
    } else if (status) {
      if (status.isFull) {
        bgColor = 'bg-red-50';
        borderColor = 'border-red-300';
      } else if (status.percentBooked >= 75) {
        bgColor = 'bg-yellow-50';
        borderColor = 'border-yellow-300';
      } else if (status.percentBooked >= 50) {
        bgColor = 'bg-orange-50';
        borderColor = 'border-orange-300';
      } else {
        bgColor = 'bg-green-50';
        borderColor = 'border-green-300';
      }
    }

    return (
      <div
        key={dateStr}
        onClick={() => setSelectedDate(day)}
        className={`min-h-24 p-2 border-2 rounded-lg cursor-pointer transition-all ${bgColor} ${borderColor} ${
          isSelected ? 'ring-2 ring-primary-500' : ''
        } ${!isCurrentMonth ? 'opacity-40' : ''} ${isToday ? 'ring-2 ring-blue-400' : ''}`}
      >
        <div className="flex justify-between items-start mb-1">
          <span className={`text-sm font-semibold ${isToday ? 'text-blue-600' : isClosed ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
            {format(day, 'd')}
          </span>
          {isToday && <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Today</span>}
        </div>

        {isClosed && isCurrentMonth ? (
          <div className="text-xs text-gray-600 font-medium">
            🚫 CLOSED
            <div className="text-xs text-gray-500 mt-1">{getClosureReason(day)}</div>
          </div>
        ) : status && isCurrentMonth && (
          <div className="space-y-1">
            <div className="text-xs font-medium">
              {status.totalAvailable}/{status.totalCapacity} spots
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {Object.entries(status.capacity).map(([size, data]) => (
                <div key={size} className={`${data.available === 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                  {size.charAt(0).toUpperCase()}: {data.available}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Annotation dots (item #18). Up to 3 visible, with a +N
            indicator when overflow. Title surfaces on hover. */}
        {isCurrentMonth && (() => {
          const dayAnnotations = getAnnotationsForDate(day);
          if (dayAnnotations.length === 0) return null;
          return (
            <div className="flex items-center gap-0.5 mt-1" title={dayAnnotations.map((a) => a.title).join(', ')}>
              {dayAnnotations.slice(0, 3).map((a) => (
                <span key={a.id} className={`inline-block w-1.5 h-1.5 rounded-full ${annotationDotClass(a.color)}`} />
              ))}
              {dayAnnotations.length > 3 && (
                <span className="text-[10px] text-gray-500 ml-0.5">+{dayAnnotations.length - 3}</span>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderGroomerCell = (day, dateStr, isCurrentMonth) => {
    const isToday = isSameDay(day, new Date());
    const isSelected = isSameDay(day, selectedDate);
    const isClosed = isDateClosed(day);

    let bgColor = 'bg-white';
    let borderColor = 'border-gray-200';
    
    if (isClosed) {
      bgColor = 'bg-gray-200';
      borderColor = 'border-gray-400';
    }

    return (
      <div
        key={dateStr}
        onClick={() => setSelectedDate(day)}
        className={`min-h-24 p-2 border-2 rounded-lg cursor-pointer transition-all ${bgColor} ${borderColor} ${
          isSelected ? 'ring-2 ring-primary-500' : ''
        } ${!isCurrentMonth ? 'opacity-40' : ''} ${isToday ? 'ring-2 ring-blue-400' : ''}`}
      >
        <div className="flex justify-between items-start mb-1">
          <span className={`text-sm font-semibold ${isToday ? 'text-blue-600' : isClosed ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
            {format(day, 'd')}
          </span>
          {isToday && <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Today</span>}
        </div>

        {/* Annotation dots (item #18) — render before the closed/
            available block so they sit just under the date number. */}
        {isCurrentMonth && getAnnotationsForDate(day).length > 0 && (
          <div className="flex items-center gap-0.5 mb-1" title={getAnnotationsForDate(day).map((a) => a.title).join(', ')}>
            {getAnnotationsForDate(day).slice(0, 3).map((a) => (
              <span key={a.id} className={`inline-block w-1.5 h-1.5 rounded-full ${annotationDotClass(a.color)}`} />
            ))}
            {getAnnotationsForDate(day).length > 3 && (
              <span className="text-[10px] text-gray-500 ml-0.5">+{getAnnotationsForDate(day).length - 3}</span>
            )}
          </div>
        )}

        {isCurrentMonth && (
          isClosed ? (
            <div className="text-xs text-gray-600 font-medium">
              🚫 CLOSED
              <div className="text-xs text-gray-500 mt-1">{getClosureReason(day)}</div>
            </div>
          ) : (
            <div className="text-xs text-gray-600">
              <div className="font-medium">
                {(() => {
                  const available = getAvailableGroomersCount(day);
                  const total = groomers.length;
                  const label = total === 1 ? 'employee' : 'employees';
                  return available === total 
                    ? `${total} ${label}` 
                    : `${available}/${total} ${label}`;
                })()}
              </div>
              <div className="text-gray-500">Click for details</div>
            </div>
          )
        )}
      </div>
    );
  };

  const renderCalendarCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dateStr = format(day, 'yyyy-MM-dd');
        const isCurrentMonth = isSameMonth(day, monthStart);
        
        days.push(
          viewMode === 'capacity'
            ? renderCapacityCell(day, dateStr, isCurrentMonth)
            : renderGroomerCell(day, dateStr, isCurrentMonth)
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day} className="grid grid-cols-7 gap-2">
          {days}
        </div>
      );
      days = [];
    }

    return <div className="space-y-2">{rows}</div>;
  };

  const renderDayDetails = () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const status = getCapacityStatus(dateStr);
    const isClosed = isDateClosed(selectedDate);

    return (
      <div className="card mt-6">
        <h3 className="text-xl font-bold mb-4">
          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </h3>

        {isClosed && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 text-red-800 font-semibold mb-2">
              <span className="text-2xl">🚫</span>
              <span>Shop Closed</span>
            </div>
            <p className="text-red-700">{getClosureReason(selectedDate)}</p>
            <p className="text-sm text-red-600 mt-2">No appointments can be booked on this date.</p>
          </div>
        )}

        {/* Roadmap item #18: annotations for the selected date. Shown
            for both open and closed days so admins can pin "Holiday —
            we're closed" notes alongside the closure reason. */}
        {(() => {
          const dayAnnotations = getAnnotationsForDate(selectedDate);
          if (dayAnnotations.length === 0) return null;
          return (
            <div className="mb-4 space-y-2">
              {dayAnnotations.map((a) => (
                <div key={a.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-3">
                  <span className={`inline-block w-3 h-3 mt-1.5 rounded-full flex-shrink-0 ${annotationDotClass(a.color)}`} />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{a.title}</div>
                    {a.description && (
                      <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">{a.description}</div>
                    )}
                    {a.is_recurring && (
                      <div className="text-xs text-gray-500 mt-1">Repeats yearly</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {!isClosed && viewMode === 'capacity' && status && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(status.capacity).map(([size, data]) => (
                <div key={size} className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">
                    {size.replace('_', ' ').toUpperCase()}
                  </div>
                  <div className="text-2xl font-bold">
                    {data.available}/{data.limit}
                  </div>
                  <div className="text-xs text-gray-500">available</div>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        data.available === 0 ? 'bg-red-500' :
                        data.available <= 2 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${(data.available / data.limit) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'groomers' && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">Available Employees</h4>
            {groomers.map(groomer => {
              const dayOfWeek = selectedDate.getDay();
              const timeOff = getTimeOffForDate(selectedDate, groomer.id);
              const isOnTimeOff = timeOff.length > 0;
              const worksToday = isGroomerWorkingOnDay(groomer.id, dayOfWeek);
              const businessClosed = isDateClosed(selectedDate);
              
              let status = 'available';
              let statusLabel = 'Available';
              let statusColor = 'text-green-600';
              let bgColor = 'bg-gray-50';
              
              if (businessClosed) {
                status = 'closed';
                statusLabel = 'Business Closed';
                statusColor = 'text-gray-500';
                bgColor = 'bg-gray-100';
              } else if (!worksToday) {
                status = 'not_scheduled';
                statusLabel = 'Not Scheduled';
                statusColor = 'text-orange-600';
                bgColor = 'bg-orange-50 border border-orange-200';
              } else if (isOnTimeOff) {
                status = 'time_off';
                statusLabel = 'Time Off';
                statusColor = 'text-red-600';
                bgColor = 'bg-red-50 border border-red-200';
              } else if (!groomer.is_active) {
                status = 'inactive';
                statusLabel = 'Inactive';
                statusColor = 'text-red-600';
                bgColor = 'bg-red-50';
              }
              
              return (
                <div key={groomer.id} className={`p-4 rounded-lg ${bgColor}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {groomer.first_name} {groomer.last_name}
                      </div>
                      {services.length > 0 && (
                        <div className="text-sm text-gray-600">
                          {services.filter(s => s.is_active).map(s => s.name).join(', ')}
                        </div>
                      )}
                      {status === 'time_off' && (
                        <div className="mt-2 text-sm">
                          <span className="text-red-700 font-semibold">Time Off</span>
                          {timeOff[0].start_time && timeOff[0].end_time && (
                            <span className="text-red-600 ml-2">
                              ({formatTo12Hour(timeOff[0].start_time)} - {formatTo12Hour(timeOff[0].end_time)})
                            </span>
                          )}
                          {isAdmin && timeOff[0].reason && (
                            <span className="text-red-600 ml-2">- {timeOff[0].reason}</span>
                          )}
                        </div>
                      )}
                      {status === 'not_scheduled' && (
                        <div className="mt-1 text-sm text-orange-600">Not scheduled to work this day</div>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className={`font-medium ${statusColor}`}>{statusLabel}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center space-x-3 mb-6">
          <CalendarIcon className="text-primary-600" size={32} />
          <h1 className="text-3xl font-bold text-gray-900">Availability Calendar</h1>
        </div>

        <div className="card">
          {renderCalendarHeader()}
          
          {loadingCapacity && (
            <div className="flex items-center justify-center py-4 mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3"></div>
              <span className="text-gray-600">Loading availability...</span>
            </div>
          )}
          
          {/* Legend */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Info size={16} className="text-gray-600" />
              <span className="font-semibold text-sm">Legend:</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-50 border-2 border-green-300 rounded"></div>
                <span>Good Availability</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-orange-50 border-2 border-orange-300 rounded"></div>
                <span>Moderate</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-50 border-2 border-yellow-300 rounded"></div>
                <span>Low Availability</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-50 border-2 border-red-300 rounded"></div>
                <span>Fully Booked</span>
              </div>
            </div>
          </div>

          {renderCalendarDays()}
          {renderCalendarCells()}
        </div>

        {renderDayDetails()}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
